const asText = (value) => (value == null ? "" : String(value).trim());

const LATIN_REFERENCE_STOP_WORDS = new Set([
  "a", "an", "the",
  "of", "in", "on", "at", "to", "by", "for", "from", "with", "without", "within",
  "off", "up", "down", "out", "into", "onto", "over", "under", "above", "below",
  "between", "among", "through", "across", "around", "near", "beside", "behind", "beyond",
  "before", "after", "during", "until", "since", "about", "against", "per", "via",
  "and", "or", "but", "as", "if", "then", "than", "so", "because", "while", "although",
  "is", "are", "was", "were", "be", "been", "being",
  "this", "that", "these", "those",
  "who", "whom", "whose", "what", "which", "when", "where", "why", "how",
  "i", "me", "my", "mine", "we", "us", "our", "ours",
  "you", "your", "yours", "he", "him", "his", "she", "her", "hers",
  "it", "its", "they", "them", "their", "theirs",
  "do", "does", "did", "have", "has", "had", "can", "could", "may", "might", "must",
  "shall", "should", "will", "would", "not", "no", "yes",
]);

const escapeRegExp = (value) => asText(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const hasCjkReferenceText = (value) => /[\p{Script=Han}]/u.test(asText(value));

export const normalizeReferenceText = (value) => asText(value)
  .normalize("NFKC")
  .toLowerCase()
  .replace(/[《》「」『』【】（）()，,。.:：、\s_\-/|｜]/g, "");

export const normalizeReferenceWords = (value) => asText(value)
  .normalize("NFKC")
  .toLowerCase()
  .replace(/[^\p{L}\p{N}]+/gu, " ")
  .trim()
  .replace(/\s+/g, " ");

const latinReferenceWords = (value) => normalizeReferenceWords(value).split(" ").filter(Boolean);

/**
 * Automatic matching must never be driven by a punctuation fragment or by
 * English function words such as "of". Manual bindings still use stable IDs.
 */
export const isSafeAutomaticReferenceCandidate = (value) => {
  const compact = normalizeReferenceText(value);
  if (compact.length < 2) return false;
  if (hasCjkReferenceText(value)) return true;
  const words = latinReferenceWords(value);
  if (!words.length) return false;
  return words.some((word) => word.length >= 2 && !LATIN_REFERENCE_STOP_WORDS.has(word));
};

const normalizedReferenceIdentity = (value) => (
  hasCjkReferenceText(value) ? normalizeReferenceText(value) : normalizeReferenceWords(value)
);

export const sameReferenceName = (left, right) => {
  const normalizedLeft = normalizedReferenceIdentity(left);
  const normalizedRight = normalizedReferenceIdentity(right);
  return Boolean(normalizedLeft && normalizedRight && normalizedLeft === normalizedRight);
};

/** Match a complete English token phrase; retain compact contains matching for CJK. */
export const referenceTextContainsCandidate = (context, candidate) => {
  if (!isSafeAutomaticReferenceCandidate(candidate)) return false;
  if (hasCjkReferenceText(candidate)) {
    const normalizedCandidate = normalizeReferenceText(candidate);
    return Boolean(normalizedCandidate && normalizeReferenceText(context).includes(normalizedCandidate));
  }
  const normalizedContext = normalizeReferenceWords(context);
  const normalizedCandidate = normalizeReferenceWords(candidate);
  return Boolean(
    normalizedContext
    && normalizedCandidate
    && ` ${normalizedContext} `.includes(` ${normalizedCandidate} `)
  );
};

export const referenceNamesOverlap = (left, right) => {
  if (sameReferenceName(left, right)) return true;
  if (!isSafeAutomaticReferenceCandidate(left) || !isSafeAutomaticReferenceCandidate(right)) return false;
  return referenceTextContainsCandidate(left, right) || referenceTextContainsCandidate(right, left);
};

const createLatinMentionPattern = (candidate) => {
  if (!isSafeAutomaticReferenceCandidate(candidate) || hasCjkReferenceText(candidate)) return null;
  const words = latinReferenceWords(candidate);
  if (!words.length) return null;
  const phrase = words.map(escapeRegExp).join("[^\\p{L}\\p{N}]+");
  return new RegExp(`(^|[^\\p{L}\\p{N}])(${phrase})(?=$|[^\\p{L}\\p{N}])`, "giu");
};

export const findReferenceMentions = (context, candidate) => {
  const text = asText(context);
  if (!text || !referenceTextContainsCandidate(text, candidate)) return [];
  if (hasCjkReferenceText(candidate)) {
    const pattern = new RegExp(escapeRegExp(candidate), "g");
    return [...text.matchAll(pattern)].map((match) => ({
      index: match.index || 0,
      length: match[0].length,
    }));
  }
  const pattern = createLatinMentionPattern(candidate);
  if (!pattern) return [];
  return [...text.matchAll(pattern)].map((match) => ({
    index: (match.index || 0) + match[1].length,
    length: match[2].length,
  }));
};

export const replaceReferenceMentions = (source, candidate, replacement) => {
  const text = asText(source);
  if (!text || !isSafeAutomaticReferenceCandidate(candidate)) return { text, replaced: false };
  if (hasCjkReferenceText(candidate)) {
    if (!text.includes(candidate)) return { text, replaced: false };
    return {
      text: text.replace(new RegExp(escapeRegExp(candidate), "g"), replacement),
      replaced: true,
    };
  }
  const pattern = createLatinMentionPattern(candidate);
  if (!pattern || !pattern.test(text)) return { text, replaced: false };
  pattern.lastIndex = 0;
  return {
    text: text.replace(pattern, (_match, prefix) => `${prefix}${replacement}`),
    replaced: true,
  };
};
